import { useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import {
  CLARIFICATION_RESPONSE_MAX,
  CLARIFICATION_RESPONSE_MIN,
  REFERRAL_NOT_AWAITING_CLARIFICATION,
  trusteeActivitySummary,
  type ReferralDto,
} from '@hv/shared';
import { ApiError } from '../api/client';
import { useMyReferralActivity, useRespondToClarification } from '../api/hooks';
import { describeError, useNotify } from '../components/Notify';

const STALE_REFERRAL = 'STALE_REFERRAL';

const formatDate = (iso: string) => new Date(iso).toLocaleString();

export function ClarificationPanel({
  referral,
  blocked,
  onSent,
  onReload,
}: {
  referral: ReferralDto;
  blocked: boolean;
  onSent: () => void;
  onReload: () => void;
}) {
  const { notify, notifyError } = useNotify();
  const respond = useRespondToClarification();
  const [text, setText] = useState('');
  const length = text.trim().length;
  const tooLong = length > CLARIFICATION_RESPONSE_MAX;
  const ready = length >= CLARIFICATION_RESPONSE_MIN && !tooLong;

  const send = async () => {
    try {
      const updated = await respond.mutateAsync({ id: referral.id, version: referral.version, response: text.trim() });
      setText('');
      notify('success', `Your response was sent to Admissions${updated.referenceNumber ? ` (${updated.referenceNumber})` : ''}`);
      onSent();
    } catch (err) {
      notifyError(err, 'We could not send your response');
      if (err instanceof ApiError && (err.code === STALE_REFERRAL || err.code === REFERRAL_NOT_AWAITING_CLARIFICATION)) onReload();
    }
  };

  return (
    <Paper component="section" aria-label="Clarification request" sx={{ p: { xs: 2, sm: 3 }, mb: 2 }} data-testid="clarification-panel">
      <Alert severity="warning" sx={{ mb: 2 }}>
        <AlertTitle>Admissions asked for clarification</AlertTitle>
        {referral.clarification && (
          <>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }} data-testid="clarification-request">
              {referral.clarification.request}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              Requested {formatDate(referral.clarification.requestedAt)}
            </Typography>
          </>
        )}
      </Alert>
      <TextField
        label="Your response to Admissions"
        multiline
        minRows={3}
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        error={tooLong}
        helperText={`${length} / ${CLARIFICATION_RESPONSE_MAX} characters${length < CLARIFICATION_RESPONSE_MIN ? ` · at least ${CLARIFICATION_RESPONSE_MIN}` : ''}`}
      />
      <Typography variant="body2" color="text.secondary" mt={1}>
        {blocked
          ? 'Save or discard your changes before sending your response.'
          : 'Sending your response returns this referral to Admissions as Submitted. To change your answers or add a child as well, choose Edit and save first.'}
      </Typography>
      <Stack direction="row" justifyContent="flex-end" mt={2}>
        <Button variant="contained" disabled={!ready || blocked || respond.isPending} onClick={() => void send()}>
          {respond.isPending ? 'Sending…' : 'Send response'}
        </Button>
      </Stack>
    </Paper>
  );
}

export function ReferralActivityLog({ referralId }: { referralId: string }) {
  const activity = useMyReferralActivity(referralId);
  return (
    <Paper component="section" aria-label="Activity" sx={{ p: { xs: 2, sm: 3 }, mt: 2 }}>
      <Typography variant="h3" component="h3" mb={1}>
        Activity
      </Typography>
      {activity.isPending ? (
        <CircularProgress size={24} aria-label="Loading activity" />
      ) : activity.error ? (
        <Alert severity="error">Could not load the activity: {describeError(activity.error)}</Alert>
      ) : (
        <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }} data-testid="referral-activity">
          {activity.data.events.map((e) => (
            <Stack
              component="li"
              key={e.id}
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              gap={{ xs: 0, sm: 2 }}
              py={0.75}
              sx={{ borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}
            >
              <Typography variant="body2">{trusteeActivitySummary(e)}</Typography>
              <Typography variant="body2" color="text.secondary" flexShrink={0}>
                {formatDate(e.at)}
              </Typography>
            </Stack>
          ))}
        </Box>
      )}
    </Paper>
  );
}
